import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { Box } from "./MuiCompat";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShareIcon from "@mui/icons-material/Share";
import { NewsItem, NewsApiResponse } from "../types/news";
import { newsService } from "../services/newsService";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const News: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [headerHeight, setHeaderHeight] = useState(56);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 8;

  // Fetch news items based on current page
  useEffect(() => {
    const fetchNewsItems = async () => {
      try {
        setLoading(true);
        const response: NewsApiResponse = await newsService.getAllNews(
          currentPage
        );
        setNewsItems(response.results);
        setTotalPages(Math.ceil(response.count / PAGE_SIZE));
        setError(null);
      } catch (err) {
        setError("Failed to load news. Please try again later.");
        console.error("Error fetching news:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItems();
  }, [currentPage]);

  // Measure header height
  useEffect(() => {
    const updateHeaderHeight = () => {
      const appBar = document.querySelector("header.MuiAppBar-root");
      if (appBar) {
        const height = appBar.getBoundingClientRect().height;
        setHeaderHeight(height);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  const handleShare = (slug: string) => {
    const url = `${window.location.origin}/news/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({ open: true, message: "Link Copied to Clipboard" });
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Sort news items by published date
  const sortedNewsItems = [...newsItems].sort((a, b) => {
    return new Date(b.published_date).getTime() - new Date(a.published_date).getTime();
  });

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "#fff",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "primary.main", color: "#fff", minHeight: "100vh" }}>
        <Box sx={{ height: headerHeight }} />
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{ textAlign: "center", color: "error.main" }}
          >
            {error}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "primary.main", color: "#fff", minHeight: "100vh" }}>
      <Box sx={{ height: headerHeight }} />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, pt: { xs: 4, md: 6 } }}>
        <Box sx={{ maxWidth: "xl", mx: "auto", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              fontWeight: "bold",
              mb: 1,
            }}
          >
            News & Announcements
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              fontWeight: "bold",
              mb: 2,
            }}
          >
            From EON
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "1.25rem", color: "grey.400", maxWidth: "32rem" }}
          >
            Stay updated with the latest news, announcements, and communications from our team.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {sortedNewsItems.map((item) => (
            <Grid item xs={12} key={item.id}>
              <Link to={`/news/${item.slug}`} style={{ textDecoration: "none" }}>
                <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        overflow: "hidden",
                        borderRadius: "8px",
                        aspectRatio: "16/9",
                      }}
                    >
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.transform = "scale(1.05)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "grey.400" }}>
                          {formatDate(item.published_date)}
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { xs: "1.5rem", md: "1.875rem" },
                            fontWeight: "bold",
                            lineHeight: "tight",
                            color: "white",
                            "&:hover": {
                              color: "pink.500",
                              textDecoration: "underline",
                            },
                            textDecoration: "none",
                            transition: "color 0.3s, text-decoration 0.3s",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "grey.400" }}>
                          {item.excerpt}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "white",
                            fontWeight: "medium",
                          }}
                        >
                          <Typography>Read More</Typography>
                          <ArrowForwardIcon
                            sx={{ ml: 1, width: 16, height: 16 }}
                          />
                        </Box>
                      </Box>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          handleShare(item.slug);
                        }}
                        sx={{
                          color: "white",
                          "&:hover": { color: "pink.500" },
                          alignSelf: "flex-start",
                        }}
                        aria-label="Share news"
                      >
                        <ShareIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          ))}
        </Grid>

        {/* Pagination Component */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "pink.500",
                    color: "white",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255, 192, 203, 0.3)",
                      zIndex: -1,
                    },
                  },
                },
              }}
            />
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default News;
